"use client";

import clsx from "clsx";
import { useRouter } from "next/navigation";
import { useState, type ReactNode } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { api, useApi } from "@/lib/useApi";
import { Icon } from "../icons";
import { Button, ErrorBox, Notice } from "../ui";
import { groupRows, settingRows, shownGroups, type Value } from "./SettingFields";
import { LayerSettings } from "./LayerSettings";
import { Select } from "../Select";
import { SectionHead, SettingsTable, type TableGroup, type TableRow } from "./SettingsTable";
import { DomainPanel } from "./DomainPanel";
import { useMessengerRow } from "./MessengerSetting";
import { useMailRow } from "./MailSetting";
import { useModeRow } from "./ModeSetting";
import { UsersPanel } from "./UsersPanel";
import { ASSET_CLASSES, FIELD_META } from "./fields";
import { useDialog } from "@/components/Dialog";

interface SettingEntry {
  label: string;
  description: string;
  scope: "user" | "global";
  value: Value;
  defaults: Value;
}
interface SettingsResp {
  user: { role: string };
  settings: Record<string, SettingEntry>;
}

/** The sections, most used first. Nav labels come from `set.nav.<key>`. */
const SECTIONS: { key: string; kind: "setting" | "layers" | "users" | "domain" }[] = [
  { key: "layers", kind: "layers" },
  { key: "preferences", kind: "setting" },
  { key: "data", kind: "setting" },
  { key: "platform", kind: "setting" },
  { key: "users", kind: "users" },
  { key: "domain", kind: "domain" },
];

/** Sections only administrators see (besides the platform-wide settings). */
const ADMIN_KINDS = new Set(["users", "domain"]);

/** The data section's providers are chosen from the provider lists, not typed. */
const DATA_LISTS = ["newsProvider", "calendarProvider"];

/** Section title/description from our own dictionary, falling back to the API's English text. */
function sectionText(t: ReturnType<typeof useI18n>["t"], k: string, entry: SettingEntry) {
  return {
    title: t(`set.section.${k}.title`, undefined, entry.label),
    description: t(`set.section.${k}.description`, undefined, entry.description),
  };
}

/** Settings (the window of the account menu): one row of section tabs, the chosen section below — one line per setting. */
export function SettingsPage() {
  const router = useRouter();
  const { t } = useI18n();
  const { data, reload } = useApi<SettingsResp>("/api/settings");
  const [section, setSection] = useState("layers");
  // the tabs depend on the settings (which sections the user may see): nothing is shown before they are known
  if (!data) return <SettingsSkeleton />;
  const admin = data.user.role === "admin";
  // a setting section when the user may see it; the users only for administrators
  const available = SECTIONS.filter((s) => (s.kind === "setting" ? !!data.settings[s.key] : !ADMIN_KINDS.has(s.kind) || admin));
  const current = available.find((s) => s.key === section) ?? available[0];
  // the administrator's sections (platform-wide settings, the users) sit at the right end of the row
  const adminOnly = (s: (typeof SECTIONS)[number]) => ADMIN_KINDS.has(s.kind) || data.settings[s.key]?.scope === "global";
  const tab = (s: (typeof SECTIONS)[number]) => (
    <button
      key={s.key}
      role="tab"
      aria-selected={current?.key === s.key}
      title={adminOnly(s) ? t("set.adminOnly") : undefined}
      onClick={(e) => {
        // phones: a tab half out of the scrolled row comes fully into view
        e.currentTarget.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        setSection(s.key);
      }}
      className={clsx("inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-lg px-2 py-1 text-[12.5px] font-medium transition", current?.key === s.key ? "bg-accent-soft text-accent-ink" : "text-muted hover:bg-surface-2 hover:text-ink")}
    >
      {adminOnly(s) && <Icon name="shield" className="h-3 w-3" />}
      {t(`set.nav.${s.key}`)}
    </button>
  );

  return (
    <div className="space-y-3">
      {/* the user's own sections, then — together, at the right end — the administrator's (marked with a shield); on
          phones one row swiped sideways, edge to edge, the administrator's after a thin line */}
      <nav aria-label={t("set.navLabel")} role="tablist" className="flex gap-1 phone:-mx-4 phone:overflow-x-auto phone:px-4 phone:scrollbar-none wide:flex-wrap">
        {available.filter((s) => !adminOnly(s)).map(tab)}
        <span className="ml-auto flex shrink-0 gap-1 phone:border-l phone:border-line phone:pl-1 wide:flex-wrap">{available.filter(adminOnly).map(tab)}</span>
      </nav>
      {current?.key === "platform" && <PlatformForm entry={data.settings.platform} onSaved={reload} />}
      {current?.kind === "setting" && current.key !== "platform" && (
        <SettingForm
          key={`${current.key}:${JSON.stringify(data.settings[current.key].value)}`}
          k={current.key}
          entry={data.settings[current.key]}
          onSaved={() => {
            reload();
            if (current.key === "preferences") router.refresh();
          }}
        />
      )}
      {current?.kind === "layers" && <LayerSettings layers={data.settings.layers} onSaved={reload} />}
      {current?.kind === "users" && <UsersPanel />}
      {current?.kind === "domain" && <DomainPanel />}
    </div>
  );
}

/** While the settings load: the tab row and a section, in their place (no tab appears before the others). */
function SettingsSkeleton() {
  return (
    <div className="space-y-3">
      <div className="skeleton h-7 w-full max-w-[640px]" />
      <div className="skeleton h-40" />
    </div>
  );
}

/**
 * The platform section: its settings, the notification bot and the server's mode as rows of the one table (those two
 * act at once), what a mode switch is doing below it.
 */
function PlatformForm({ entry, onSaved }: { entry: SettingEntry; onSaved: () => void }) {
  const messenger = useMessengerRow();
  const mail = useMailRow();
  const mode = useModeRow();
  const rows = [messenger, mail, ...(mode.row ? [mode.row] : [])];
  return <SettingForm key={JSON.stringify(entry.value)} k="platform" entry={entry} onSaved={onSaved} extra={{ rows, below: mode.status }} />;
}

/** A section's form; `extra`: rows of its own after the settings' (saved by themselves) and what shows below the table. */
function SettingForm({ k, entry, onSaved, extra }: { k: string; entry: SettingEntry; onSaved: () => void; extra?: { rows: TableRow[]; below: ReactNode } }) {
  const [value, setValue] = useState<Value>(entry.value);
  const [status, setStatus] = useState<{ tone: "good" | "bad"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const { t, f, locale } = useI18n();
  const dialog = useDialog();
  const { title, description } = sectionText(t, k, entry);
  const { data: providers } = useApi<Providers>(k === "data" ? "/api/providers" : null);
  const dirty = JSON.stringify(value) !== JSON.stringify(entry.value);

  async function save() {
    setBusy(true);
    setStatus(null);
    try {
      await api(`/api/settings/${k}`, { method: "PUT", json: value });
      setStatus({ tone: "good", text: t("set.saved") });
      onSaved();
    } catch (e) {
      setStatus({ tone: "bad", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    if (!(await dialog.confirm(t("set.confirmReset", { name: locale === "en" ? title.toLowerCase() : title }), { danger: true }))) return;
    await api(`/api/settings/${k}`, { method: "DELETE" });
    setStatus({ tone: "good", text: t("set.defaultsRestored") });
    onSaved();
  }

  const meta = FIELD_META[k] ?? {};
  const i18n = { t, f };
  const rows: TableRow[] = [...settingRows(i18n, { value, defaults: entry.defaults, meta, skip: k === "data" ? DATA_LISTS : [], onChange: setValue }), ...(extra?.rows ?? [])];
  // the data section: the provider per asset class as one group, then its other choices; else the section's settings,
  // then each of its groups
  const nested = shownGroups(value, meta).map((g) => ({ key: `${k}.${g}`, title: t(`set.group.${k}.${g}`), rows: groupRows(i18n, g, { value: value[g] as Value, defaults: entry.defaults[g] as Value, meta, onChange: (v) => setValue({ ...value, [g]: v }) }) }));
  const groups: TableGroup[] = k === "data" ? dataGroups(t, value, entry.defaults, providers, setValue, rows) : [{ key: k, rows }, ...nested];

  return (
    <section>
      <SectionHead title={title} description={description} />
      {k === "data" && !providers ? <div className="skeleton mt-2 h-40" /> : <SettingsTable groups={groups} />}
      {extra?.below && <div className="mt-1.5 space-y-1.5">{extra.below}</div>}
      {status && <div className="mt-1.5">{status.tone === "bad" ? <ErrorBox>{status.text}</ErrorBox> : <Notice tone="good">✓ {status.text}</Notice>}</div>}
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 border-t border-line pt-1.5">
        <Button size="xs" variant="primary" onClick={save} disabled={!dirty || busy}>{busy ? t("set.saving") : t("set.saveChanges")}</Button>
        {dirty && <Button size="xs" onClick={() => setValue(entry.value)}>{t("set.discard")}</Button>}
        <Button size="xs" variant="ghost" onClick={reset} className="ml-auto">{t("set.restoreDefaults")}</Button>
      </div>
    </section>
  );
}

type Named = { id: string; name: string };
type Providers = { providers: (Named & { kind: string; assetClasses: string[] })[]; news: Named[]; calendar: Named[] };
type T = ReturnType<typeof useI18n>["t"];

/** A provider choice: the offered providers, the current one when it is not offered, and "none" when that is allowed. */
function ProviderSelect({ label, current, options, none, onChange }: { label: string; current: string; options: Named[]; none?: string; onChange: (id: string) => void }) {
  const { t } = useI18n();
  const unknown = !options.some((p) => p.id === current) && current !== "none";
  const choices = [...options.map((p) => ({ value: p.id, label: p.name })), ...(unknown ? [{ value: current, label: current }] : []), ...(none ? [{ value: "none", label: t(none) }] : [])];
  return <Select label={label} value={current} options={choices} onChange={onChange} className="w-full" />;
}

/** A provider's name (its id when unknown; the "none" choice's label). */
function providerName(t: T, list: Named[] | undefined, id: string, none?: string): string {
  if (id === "none" && none) return t(none);
  return list?.find((p) => p.id === id)?.name ?? id;
}

/** The data section's groups: the market-data provider per asset class; the news, the calendar and the other settings. */
function dataGroups(t: T, value: Value, defaults: Value, providers: Providers | null | undefined, onChange: (v: Value) => void, rest: TableRow[]): TableGroup[] {
  const map = value.providerByAssetClass as Record<string, string>;
  const defaultMap = defaults.providerByAssetClass as Record<string, string>;
  const row = (key: string, label: string, description: string, current: string, fallback: string, options: Named[], set: (id: string) => void, none?: string): TableRow => ({
    key,
    label,
    description,
    input: <ProviderSelect label={label} current={current} options={options} none={none} onChange={set} />,
    default: providerName(t, options, fallback, none),
    changed: current !== fallback,
    onDefault: () => set(fallback),
  });
  const market = ASSET_CLASSES.map((ac) =>
    row(
      `market.${ac}`,
      t(`assetClass.${ac}`),
      "",
      map[ac],
      defaultMap[ac],
      providers?.providers.filter((p) => p.kind === "market_data" && p.assetClasses.includes(ac)) ?? [],
      (id) => onChange({ ...value, providerByAssetClass: { ...map, [ac]: id } }),
    ),
  );
  const list = (field: "newsProvider" | "calendarProvider", options: Named[] | undefined, none: string) =>
    row(field, t(`set.data.${field}`), t(`set.data.${field}Hint`), String(value[field]), String(defaults[field]), options ?? [], (id) => onChange({ ...value, [field]: id }), none);
  return [
    { key: "market", title: t("set.data.providerTitle"), rows: market },
    { key: "other", title: t("set.data.otherTitle"), rows: [list("newsProvider", providers?.news, "set.data.newsNone"), list("calendarProvider", providers?.calendar, "set.data.calendarNone"), ...rest] },
  ];
}
